/**
 * F11.92: Contexto global de billetera con SSE.
 * Una sola conexión SSE para toda la app, estado compartido.
 */
import { createContext, useContext, useEffect, useState, useRef } from "react"
import { api } from "../api/client"
import { useAuth } from "../auth/AuthContext"

const WalletContext = createContext(null)

export function WalletProvider({ children }) {
  const { user, loading: authLoading } = useAuth()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const esRef = useRef(null)
  const reconnectTimeoutRef = useRef(null)

  // Carga inicial: re-run cuando cambia el usuario autenticado
  useEffect(() => {
    if (authLoading) return
    if (!user) { setData(null); setLoading(false); return }
    let mounted = true
    setLoading(true)
    api.get("/api/wallets/me")
      .then((r) => { if (mounted) setData(r) })
      .catch(() => {})
      .finally(() => { if (mounted) setLoading(false) })
    return () => { mounted = false }
  }, [user?.id, authLoading])

  // SSE: re-conecta cuando cambia el usuario autenticado
  useEffect(() => {
    if (authLoading || !user) return
    const token = localStorage.getItem("token")
    if (!token) return

    const connectSSE = () => {
      // Cerrar conexión previa si existe
      if (esRef.current) {
        esRef.current.close()
        esRef.current = null
      }

      const es = new EventSource(`/api/wallets/stream?jwt=${encodeURIComponent(token)}`)
      esRef.current = es

      es.addEventListener("wallet_update", (e) => {
        try {
          const payload = JSON.parse(e.data)
          console.log("[SSE Global] Wallet update recibido:", payload)
          setData(prev => ({
            ...(prev || {}),
            total: payload.total,
            totalPen: payload.total,
            real: payload.real,
            realPen: payload.real,
            promo: payload.promo,
            promoPen: payload.promo,
          }))
        } catch (err) {
          console.error("[SSE Global] Error parseando:", err)
        }
      })

      es.addEventListener("error", (e) => {
        if (es.readyState === EventSource.CLOSED) {
          console.warn("[SSE Global] Conexión cerrada, reintentando en 5s...")
          // Reintento con backoff
          if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current)
          reconnectTimeoutRef.current = setTimeout(() => {
            connectSSE()
          }, 5000)
        }
      })
    }

    connectSSE()

    return () => {
      if (esRef.current) {
        esRef.current.close()
        esRef.current = null
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current)
      }
    }
  }, [user?.id, authLoading])

  return (
    <WalletContext.Provider value={{ data, loading }}>
      {children}
    </WalletContext.Provider>
  )
}

export function useWallet() {
  const ctx = useContext(WalletContext)
  if (!ctx) throw new Error("useWallet debe usarse dentro de WalletProvider")
  return ctx
}
